/**
 * Central HTTP helper: timeout, non-2xx handling, JSON or raw string (e.g. JWT) bodies.
 */

export class ApiRequestError extends Error {
  constructor(message, opts = {}) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = opts.status;
    this.statusText = opts.statusText;
    this.bodySnippet = opts.bodySnippet;
    this.code = opts.code;
  }
}

export const DEFAULT_API_TIMEOUT_MS = 120000;
export const UPLOAD_API_TIMEOUT_MS = 180000;

function parseResponseBody(text) {
  const t = (text || '').trim();
  if (!t) {
    return null;
  }
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

/**
 * @param {string} url
 * @param {RequestInit & { timeoutMs?: number }} [options] timeoutMs aborts the request; other fields passed to fetch()
 * @returns {Promise<unknown>} Parsed JSON, or a string (e.g. JWT) if body is not JSON
 */
export async function apiFetch(url, options = {}) {
  const { timeoutMs = DEFAULT_API_TIMEOUT_MS, ...init } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const reqInit = { ...init, signal: controller.signal };

  if (reqInit.body instanceof FormData && reqInit.headers) {
    const h = new Headers(reqInit.headers);
    h.delete('Content-Type');
    reqInit.headers = h;
  }

  let res;
  try {
    res = await fetch(url, reqInit);
  } catch (e) {
    clearTimeout(timer);
    if (e.name === 'AbortError') {
      throw new ApiRequestError('Request timed out. Please try again.', { code: 'aborted' });
    }
    throw new ApiRequestError(e.message || 'Network error. Please try again.', { code: 'network' });
  }

  clearTimeout(timer);

  const text = await res.text();
  const data = parseResponseBody(text);

  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    if (res.status === 503 || res.status === 502) {
      msg = 'Service temporarily unavailable. Please try again in a moment.';
    } else if (res.status === 504) {
      msg = 'The server took too long to respond. Please try again.';
    } else if (res.status >= 500) {
      msg = 'Server error. Please try again.';
    }
    throw new ApiRequestError(msg, {
      code: 'http',
      status: res.status,
      statusText: res.statusText,
      bodySnippet: text.slice(0, 300)
    });
  }

  return data;
}
